/**
 * Word-level comparison of two texts, to show what a suggested rewrite changes.
 * Words, spaces and punctuation are compared as separate pieces, so a corrected
 * typo marks one word and not the whole sentence.
 */

// A mention placeholder, a word (letters, digits, with inner ' or -), white space,
// or a single other character.
const TOKEN =
  /\[\[m:\d+\]\]|[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*|\s+|[^\s\p{L}\p{N}]/gu;

// Above this many table cells the comparison is skipped (see diffWords).
const MAX_CELLS = 4000000;

const tokenize = (text) => (text || "").match(TOKEN) || [];

const push = (segments, type, text) => {
  if (!text) return;
  const last = segments[segments.length - 1];
  if (last && last.type === type) last.text += text;
  else segments.push({ type, text });
};

/**
 * Compares the original text with the rewrite.
 *
 * @param {string} original
 * @param {string} rewrite
 * @returns {Array<{ type: "same"|"added"|"removed", text: string }>|null} segments in
 *   reading order (removed text comes before what replaced it), or null when the
 *   texts are too long to compare; show the rewrite plainly then.
 */
export function diffWords(original, rewrite) {
  const a = tokenize(original);
  const b = tokenize(rewrite);

  // What both texts start and end with needs no comparing.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start])
    start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }

  const rows = endA - start;
  const columns = endB - start;
  if (rows * columns > MAX_CELLS) return null;

  // Longest common subsequence of the middle parts.
  const width = columns + 1;
  const table = new Uint32Array((rows + 1) * width);
  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = columns - 1; j >= 0; j -= 1) {
      table[i * width + j] =
        a[start + i] === b[start + j]
          ? table[(i + 1) * width + j + 1] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + j + 1]);
    }
  }

  const segments = [];
  push(segments, "same", a.slice(0, start).join(""));

  let i = 0;
  let j = 0;
  // Changes between two unchanged pieces are collected, then written as
  // "removed, added" so a replaced word reads old-then-new.
  let removed = "";
  let added = "";
  const flush = () => {
    push(segments, "removed", removed);
    push(segments, "added", added);
    removed = "";
    added = "";
  };
  while (i < rows || j < columns) {
    if (i < rows && j < columns && a[start + i] === b[start + j]) {
      flush();
      push(segments, "same", a[start + i]);
      i += 1;
      j += 1;
    } else if (
      j < columns &&
      (i === rows || table[i * width + j + 1] >= table[(i + 1) * width + j])
    ) {
      added += b[start + j];
      j += 1;
    } else {
      removed += a[start + i];
      i += 1;
    }
  }
  flush();

  push(segments, "same", a.slice(endA).join(""));
  return segments;
}

/**
 * The segments to show. Removed white space is left out (it would only show as a
 * gap), and with `showRemoved: false` so is everything that was removed, which
 * leaves the rewrite itself with its additions marked.
 *
 * @param {Array<{ type: string, text: string }>} segments - from diffWords
 * @param {{ showRemoved?: boolean }} [options]
 */
export function visibleSegments(segments, { showRemoved = true } = {}) {
  return (segments || []).filter(
    (segment) =>
      segment.type !== "removed" || (showRemoved && segment.text.trim() !== ""),
  );
}

/** True when the two texts differ in more than nothing (there is something to mark). */
export function hasChanges(segments) {
  return (segments || []).some((segment) => segment.type !== "same");
}

/**
 * The worker's text from the data part of a logged text-check request
 * ("Report text:\n<<<\n...\n>>>"), or null when it is not there.
 */
export function reportTextFromPrompt(userPrompt) {
  const match = /Report text:\n<<<\n([\s\S]*)\n>>>/.exec(userPrompt || "");
  return match ? match[1] : null;
}
